import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const PaymentSuccess = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshAccess } = useAuth()
  const [status, setStatus] = useState('checking') // checking, success, error
  const [message, setMessage] = useState('Overujem platbu...')
  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    if (sessionId && user) {
      checkPaymentStatus()
    }
  }, [sessionId, user])

  const checkPaymentStatus = async () => {
    let attempts = 0
    const maxAttempts = 5
    const pollInterval = 2000 // 2 seconds

    const poll = async () => {
      try {
        // Get session safely
        let accessToken = null
        try {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
          if (sessionError) {
            console.error('[Payment] Session error:', sessionError.message)
          } else if (sessionData?.session?.access_token) {
            accessToken = sessionData.session.access_token
          }
        } catch (sessionErr) {
          console.error('[Payment] Session fetch error:', sessionErr.message || String(sessionErr))
        }

        if (!accessToken) {
          // Only show error if max attempts reached
          if (attempts >= maxAttempts) {
            setStatus('error')
            setMessage('Autentifikácia zlyhala. Prosím prihláste sa znova.')
          } else {
            attempts++
            setMessage(`Overujem platbu... (pokus ${attempts}/${maxAttempts})`)
            setTimeout(poll, pollInterval)
          }
          return
        }

        const response = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        })

        if (!response.ok) {
          // Retry on HTTP error instead of immediately failing
          if (attempts < maxAttempts) {
            attempts++
            setMessage(`Overujem platbu... (pokus ${attempts}/${maxAttempts})`)
            setTimeout(poll, pollInterval)
            return
          }
          throw new Error('Chyba pri kontrole stavu platby')
        }

        const data = await response.json()

        if (data.payment_status === 'paid') {
          setStatus('success')
          setMessage('Platba bola úspešná! Aktivujem váš prístup...')
          
          console.log('[PaymentSuccess] ✅ Payment confirmed - refreshing access from Supabase')
          
          // IMPORTANT: Refresh access from Supabase (single source of truth)
          await refreshAccess()
          
          // Wait a moment for UI to update
          await new Promise(resolve => setTimeout(resolve, 500))
          
          console.log('[PaymentSuccess] 🎯 Navigating to dashboard')
          navigate('/dashboard')
        } else if (data.is_pending_bank_transfer === true) {
          // Bank transfer created; funds not yet received. Do NOT unlock.
          setStatus('pending_bank_transfer')
          setMessage('Čakáme na pripísanie platby. Prístup k testom aktivujeme po jej potvrdení.')
        } else if (data.status === 'expired') {
          setStatus('error')
          setMessage('Platobná session vypršala. Skúste to prosím znova.')
        } else if (data.payment_status === 'unpaid') {
          // Card payment attempted but not completed (declined, cancelled,
          // or interrupted). NEVER treat this as a pending bank transfer.
          setStatus('error')
          setMessage('Platba kartou nebola dokončená. Skúste to prosím znova alebo použite inú kartu.')
        } else if (attempts < maxAttempts) {
          attempts++
          setMessage(`Overujem platbu... (pokus ${attempts}/${maxAttempts})`)
          setTimeout(poll, pollInterval)
        } else {
          setStatus('error')
          setMessage('Nepodarilo sa overiť platbu. Skontrolujte svoj email alebo kontaktujte podporu.')
        }
      } catch (error) {
        console.error('Payment check error:', error?.message || String(error))
        
        // Only show error if max attempts reached
        if (attempts >= maxAttempts) {
          setStatus('error')
          setMessage('Nastala chyba pri kontrole platby.')
        } else {
          // Retry on error
          attempts++
          setMessage(`Overujem platbu... (pokus ${attempts}/${maxAttempts})`)
          setTimeout(poll, pollInterval)
        }
      }
    }

    poll()
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          {status === 'checking' && (
            <div data-testid="checking-payment">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overujem platbu</h2>
              <p className="text-gray-600">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="payment-success">
              <div className="text-6xl mb-6">✅</div>
              <h2 className="text-2xl font-bold text-green-600 mb-4">Platba úspešná!</h2>
              <p className="text-gray-600 mb-2">Váš prémium prístup bol aktivovaný.</p>
              
              {/* Usage Reminder */}
              <div className="mt-6 mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
                <p className="text-sm text-gray-800 leading-relaxed">
                  <strong className="text-amber-900">Pripomienka:</strong> Prihlasovacie údaje sú určené len pre vašu školu 
                  a nesmú byť zdieľané mimo nej.
                </p>
              </div>
              
              <p className="text-sm text-gray-500 mb-6">Presmerovávam na testy...</p>
            </div>
          )}

          {status === 'pending_bank_transfer' && (
            <div data-testid="payment-pending-bank-transfer">
              <div className="text-6xl mb-6">⏳</div>
              <h2 className="text-2xl font-bold text-blue-700 mb-4">Čakáme na platbu</h2>
              <p className="text-gray-700 mb-4">{message}</p>
              <p className="text-sm text-gray-600 mb-6">
                Pokyny na prevod (IBAN a referenciu) uvidíte priamo na platobnej stránke Stripe po dokončení objednávky. Po pripísaní platby vás upozorníme e-mailom a prístup k testom sa aktivuje automaticky.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                data-testid="pending-back-to-dashboard"
              >
                Späť na dashboard
              </button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="payment-error">
              <div className="text-6xl mb-6">❌</div>
              <h2 className="text-2xl font-bold text-red-600 mb-4">Chyba platby</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Skúsiť znova
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
                >
                  Späť na dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccess
